import { toast } from "sonner";
import { Download, Megaphone } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { api, errMsg, useApi } from "@/lib/api";
import { fmtDateTime } from "@/lib/crm";

const STATUS = { attivo: "Attivo", assente: "Assente", revocato: "Revocato" };
const OUTCOME = { inviato: "Inviato", inviato_eccezione: "Inviato con eccezione", bloccato: "Bloccato" };
const MEDIUM = { email: "Email", sms: "SMS", whatsapp: "WhatsApp", telefono: "Telefono" };
const CHANNEL = { whatsapp: "WhatsApp", sms: "SMS", copia: "Testo copiato", gmail: "Gmail", outlook: "Outlook", email_crm: "Email dal CRM", email_auguri_automatica: "Auguri automatici" };

const exportCsv = async () => {
  try {
    const { data } = await api.get("/promo-log/export.csv", { responseType: "blob" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(data);
    a.download = "registro_invii_promozionali.csv";
    a.click();
    URL.revokeObjectURL(a.href);
  } catch (e) { toast.error(errMsg(e)); }
};

export const PromoLog = ({ f, setF, save }) => {
  const { data = [] } = useApi("/promo-log");
  const toggle = (v) => { const n = { ...f, promo_exceptions_enabled: v }; setF(n); save(n); };
  return (
    <section className="bg-white border border-slate-200/80 rounded-xl p-6 space-y-5" data-testid="promo-log-section">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h3 className="font-display font-semibold flex items-center gap-2"><Megaphone className="h-4 w-4 text-sky-600" />Registro invii promozionali</h3>
          <p className="text-sm text-slate-500 mt-1">Ogni comunicazione promozionale, inviata o bloccata, con lo stato del consenso marketing al momento dell'invio. Le comunicazioni operative non sono registrate qui.</p>
        </div>
        <Button data-testid="promo-log-export-btn" variant="outline" onClick={exportCsv} className="rounded-full"><Download className="h-4 w-4 mr-2" />Esporta CSV</Button>
      </div>
      <div className="flex items-start justify-between gap-6 rounded-lg border border-amber-200 bg-amber-50/60 p-4">
        <div>
          <p className="font-medium text-sm">Consenti eccezioni motivate</p>
          <p className="text-xs text-slate-600 mt-1">Disattivato (consigliato): senza consenso attivo nessun invio promozionale può partire. Attivalo solo se il tuo legale ha individuato una base giuridica diversa dal consenso: potrai confermare l'eccezione caso per caso e verrà registrata.</p>
        </div>
        <Switch data-testid="promo-exceptions-switch" checked={!!f.promo_exceptions_enabled} onCheckedChange={toggle} />
      </div>
      <div className="overflow-x-auto max-h-96 overflow-y-auto">
        <table className="w-full text-sm" data-testid="promo-log-table">
          <thead><tr className="text-left text-xs uppercase tracking-wider text-slate-500 border-b border-slate-100">
            <th className="py-2 pr-4 font-medium">Data</th><th className="py-2 pr-4 font-medium">Cliente</th><th className="py-2 pr-4 font-medium">Canale</th>
            <th className="py-2 pr-4 font-medium">Oggetto</th><th className="py-2 pr-4 font-medium">Consenso</th><th className="py-2 font-medium">Esito</th>
          </tr></thead>
          <tbody>
            {data.map((x) => {
              const bad = x.consent_status !== "attivo";
              return (
                <tr key={x.log_id} data-testid={`promo-log-row-${x.log_id}`} data-unconsented={bad} className={`border-b border-slate-100 last:border-0 ${bad ? "bg-rose-50/70" : ""}`}>
                  <td className="py-2 pr-4 font-mono text-xs whitespace-nowrap">{fmtDateTime(x.at)}</td>
                  <td className="py-2 pr-4">{x.client_name}</td>
                  <td className="py-2 pr-4 text-xs">{CHANNEL[x.channel] || x.channel}{x.channel === "copia" && x.medium ? ` → ${MEDIUM[x.medium]}` : ""}</td>
                  <td className="py-2 pr-4 text-xs text-slate-600 max-w-[220px] truncate">{x.subject}</td>
                  <td className={`py-2 pr-4 text-xs font-medium ${bad ? "text-rose-700" : "text-emerald-700"}`}>{STATUS[x.consent_status]}</td>
                  <td className={`py-2 text-xs font-medium ${x.outcome === "bloccato" ? "text-slate-500" : x.outcome === "inviato_eccezione" ? "text-amber-700" : "text-slate-800"}`}>{OUTCOME[x.outcome]}{x.reason && <span className="block font-normal text-[11px] text-slate-500">{x.reason}</span>}{x.exception_reason && <span data-testid={`promo-log-exception-${x.log_id}`} className="block font-normal text-[11px] text-amber-800">Motivazione: {x.exception_reason}</span>}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {data.length === 0 && <p className="text-center text-sm text-slate-400 py-6" data-testid="promo-log-empty">Nessun invio promozionale registrato.</p>}
      </div>
    </section>
  );
};
